import React, {FC, useEffect, useMemo, useState} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import {Alert, Button, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {FieldTile, PageHeader, Panel, PublicShell} from "../Components/ui";

type PublicRegistrationInfo = {
    eventId?: number | null;
    eventName?: string | null;
    eventCode?: string | null;
    registrationStatus?: string | number | null;
    participant?: {
        fullName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        company?: string | null;
    } | null;
};

const encodeQueryValue = (value: string) => encodeURIComponent(value.trim());

export const buildPublicRegistrationInfoUrl = (eventIdentifier: string, token: string) =>
    `/api/public/events/${encodeURIComponent(eventIdentifier)}/registration-codes/info?token=${encodeQueryValue(token)}`;

export const buildPublicCertificateRequestUrl = (eventIdentifier: string, token: string) =>
    `/api/public/events/${encodeURIComponent(eventIdentifier)}/certificates/request?token=${encodeQueryValue(token)}`;

const extractPublicErrorMessage = async (response: Response, fallback: string) => {
    try {
        const payload = await response.json();
        const errors = payload?.errors;

        if (Array.isArray(errors) && errors.length > 0) {
            return errors.map(error => error?.message ?? String(error)).join(" ");
        }

        return payload?.message ?? payload?.detail ?? payload?.title ?? fallback;
    } catch {
        return fallback;
    }
};

const participantName = (info?: PublicRegistrationInfo | null) => {
    const participant = info?.participant;
    const fullName = participant?.fullName?.trim();

    if (fullName) {
        return fullName;
    }

    return `${participant?.firstName?.trim() ?? ""} ${participant?.lastName?.trim() ?? ""}`.trim();
};

const PublicCertificateRequestPage: FC = () => {
    const {t} = useTranslation();
    const {eventIdentifier = ""} = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token")?.trim() ?? "";
    const [info, setInfo] = useState<PublicRegistrationInfo>();
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [error, setError] = useState<string>();
    const [requested, setRequested] = useState(false);
    const name = useMemo(() => participantName(info), [info]);

    useEffect(() => {
        if (!eventIdentifier || !token) {
            return;
        }

        let cancelled = false;

        const loadInfo = async () => {
            setLoadingInfo(true);
            setError(undefined);

            try {
                const response = await fetch(buildPublicRegistrationInfoUrl(eventIdentifier, token), {
                    credentials: "include"
                });

                if (!response.ok) {
                    throw new Error(await extractPublicErrorMessage(response, t("certificate-request-load-failed", {
                        defaultValue: "Registration details could not be loaded."
                    })));
                }

                const payload = await response.json() as PublicRegistrationInfo;
                if (!cancelled) {
                    setInfo(payload);
                }
            } catch (loadError) {
                if (!cancelled) {
                    setError(loadError instanceof Error
                        ? loadError.message
                        : t("certificate-request-load-failed", {
                            defaultValue: "Registration details could not be loaded."
                        }));
                }
            } finally {
                if (!cancelled) {
                    setLoadingInfo(false);
                }
            }
        };

        void loadInfo();

        return () => {
            cancelled = true;
        };
    }, [eventIdentifier, t, token]);

    const handleRequest = async () => {
        if (!eventIdentifier || !token) {
            setError(t("certificate-request-missing-token", {
                defaultValue: "Certificate request link is missing a registration token."
            }));
            return;
        }

        setRequesting(true);
        setError(undefined);

        try {
            const response = await fetch(buildPublicCertificateRequestUrl(eventIdentifier, token), {
                method: "POST",
                credentials: "include"
            });

            if (!response.ok) {
                throw new Error(await extractPublicErrorMessage(response, t("certificate-request-submit-failed", {
                    defaultValue: "Certificate request could not be submitted."
                })));
            }

            setRequested(true);
        } catch (requestError) {
            setError(requestError instanceof Error
                ? requestError.message
                : t("certificate-request-submit-failed", {
                    defaultValue: "Certificate request could not be submitted."
                }));
        } finally {
            setRequesting(false);
        }
    };

    return (
        <PublicShell live>
            <PageHeader
                title={t("certificate-request-title", {defaultValue: "Certificate request"})}
                description={t("certificate-request-description", {
                    defaultValue: "Use your event QR link to request a certificate after the event is completed."
                })}
            />
            <Panel>
                <div className="ati-stack">

                    {!token ? (
                        <Alert
                            type="warning"
                            showIcon
                            message={t("certificate-request-missing-token", {
                                defaultValue: "Certificate request link is missing a registration token."
                            })}
                        />
                    ) : null}

                    {error ? <Alert type="error" showIcon message={error}/> : null}

                    {info ? (
                        <FieldTile label={info.eventName || info.eventId || info.eventCode || eventIdentifier}>
                            {name ? <Typography.Text strong>{name}</Typography.Text> : null}
                            {info.participant?.company ? (
                                <div><Typography.Text type="secondary">{info.participant.company}</Typography.Text></div>
                            ) : null}
                        </FieldTile>
                    ) : null}

                    {requested ? (
                        <Alert
                            type="success"
                            showIcon
                            message={t("certificate-request-success", {
                                defaultValue: "Certificate request received."
                            })}
                        />
                    ) : (
                        <Button
                            type="primary"
                            loading={loadingInfo || requesting}
                            disabled={!token || loadingInfo || !!error}
                            onClick={handleRequest}
                        >
                            {t("request-certificate", {defaultValue: "Request certificate"})}
                        </Button>
                    )}
                </div>
            </Panel>
        </PublicShell>
    );
};

export default PublicCertificateRequestPage;
